import { useQuery } from '@tanstack/react-query';
import { countries } from '@topcon/topcon-utils';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { postV1AddressSuggest } from '../api/generated/pm';
import { GeolocationStatusEnum, Country, Coordinates } from '../types/geolocation.types';
import {
  getCurrentPosition,
  getGeolocationPermissionState,
  isGeolocationSupported,
  subscribeToGeolocationPermission,
} from '../utils/geolocation';

export type PermissionState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';

type GeolocationContextValue = {
  status: GeolocationStatusEnum;
  country: Country | null;
  isLoading: boolean;
  startCountryDetection: () => void;
  retryReverseGeocode: () => void;
};

const GeolocationContext = createContext<GeolocationContextValue | null>(null);

export function GeolocationProvider({ children }: { children: React.ReactNode }) {
  const [permissionState, setPermissionState] = useState<PermissionState>('unknown');
  const [isResolvingCountry, setIsResolvingCountry] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const coordinatesRef = useRef<Coordinates | null>(null);

  const geolocationQuery = useQuery({
    queryKey: ['geolocation', 'country'],
    queryFn: async () => {
      try {
        let coordinates = coordinatesRef.current;
        if (!coordinates) {
          setIsResolvingCountry(false);
          coordinates = await getCurrentPosition();
          coordinatesRef.current = coordinates;
          setPermissionState('granted');
        }

        setIsResolvingCountry(true);

        const { data } = await postV1AddressSuggest({
          body: { latitude: coordinates.latitude, longitude: coordinates.longitude },
          throwOnError: true,
        });
        const countryCode = data.items[0]?.country;
        if (!countryCode) {
          throw new Error('No country resolved');
        }
        setIsResolvingCountry(false);
        return { coordinates, countryCode };
      } catch (err) {
        setIsResolvingCountry(false);
        console.error('[useGeolocation] Detection failed:', err);

        const isPermissionDenied =
          (typeof GeolocationPositionError !== 'undefined' &&
            err instanceof GeolocationPositionError &&
            err.code === GeolocationPositionError.PERMISSION_DENIED) ||
          (err as { code?: number })?.code === 1;

        if (isPermissionDenied) {
          coordinatesRef.current = null;
          setPermissionState('denied');
        }
        throw err;
      }
    },
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const startCountryDetection = useCallback(() => {
    if (
      !isGeolocationSupported() ||
      permissionState === 'denied' ||
      permissionState === 'unsupported'
    ) {
      return;
    }
    setEnabled(true);
  }, [permissionState]);

  const retryReverseGeocode = useCallback(() => {
    void geolocationQuery.refetch();
  }, [geolocationQuery]);

  const status = useMemo(() => {
    if (permissionState === 'unsupported') {
      return GeolocationStatusEnum.Unsupported;
    }

    if (permissionState === 'denied') {
      return GeolocationStatusEnum.PermissionDenied;
    }

    if (geolocationQuery.isSuccess) {
      return GeolocationStatusEnum.Success;
    }

    if (geolocationQuery.isError) {
      return GeolocationStatusEnum.Error;
    }

    if (geolocationQuery.isFetching) {
      return isResolvingCountry || permissionState === 'granted'
        ? GeolocationStatusEnum.ResolvingCountry
        : GeolocationStatusEnum.ResolvingLocation;
    }

    return GeolocationStatusEnum.Idle;
  }, [
    permissionState,
    geolocationQuery.isSuccess,
    geolocationQuery.isError,
    geolocationQuery.isFetching,
    isResolvingCountry,
  ]);

  const country = useMemo(() => {
    const countryCode = geolocationQuery.data?.countryCode;
    if (!countryCode) return null;
    return countries.getCountry(countryCode) ?? null;
  }, [geolocationQuery.data?.countryCode]);

  const { isFetching: isLoading } = geolocationQuery;

  // Get the user's geolocation permission state on mount and subscribe to changes
  useEffect(() => {
    void (async () => {
      const permission = await getGeolocationPermissionState();
      setPermissionState((permission as PermissionState) ?? 'unknown');
    })();

    const unsubscribe = subscribeToGeolocationPermission((state) => {
      setPermissionState(state);
      if (state === 'denied') {
        coordinatesRef.current = null;
      }
    });

    return unsubscribe;
  }, []);

  const value = useMemo(
    () => ({
      status,
      country,
      isLoading,
      startCountryDetection,
      retryReverseGeocode,
    }),
    [status, country, isLoading, startCountryDetection, retryReverseGeocode]
  );

  return <GeolocationContext.Provider value={value}>{children}</GeolocationContext.Provider>;
}

export function useGeolocation() {
  const ctx = useContext(GeolocationContext);
  if (!ctx) throw new Error('useGeolocation must be used within GeolocationProvider');
  return ctx;
}
